import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AddJobDialog from "@/components/AddJobDialog";
import BulkJobImportDialog from "@/components/BulkJobImportDialog";
import { formatPostedAge, formatPostedAbsolute, postedRangeQuery, POSTED_PRESETS, SORT_OPTIONS } from "@/lib/postedDate";
import {
  MagnifyingGlass, Plus, UploadSimple, ArrowsCounterClockwise,
  Buildings, MapPin, Calendar, Link as LinkIcon, ShareNetwork, Check,
} from "@phosphor-icons/react";

const STATUS_TONE = {
  Active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Expired: "bg-amber-50 text-amber-800 border-amber-200",
  Closed: "bg-slate-100 text-slate-600 border-slate-200",
  Unavailable: "bg-red-50 text-red-800 border-red-200",
  Archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" }); }
  catch { return d; }
};

export default function JobLibrary() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [workFilter, setWorkFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [postedPreset, setPostedPreset] = useState("all");
  const [sortBy, setSortBy] = useState("discovered_desc");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page, page_size: pageSize, sort_by: sortBy };
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      if (workFilter) params.work_arrangement = workFilter;
      if (sourceFilter) params.source = sourceFilter;
      const posted = postedRangeQuery(postedPreset);
      if (posted.posted_after) params.posted_after = posted.posted_after;
      if (posted.posted_before) params.posted_before = posted.posted_before;
      const { data } = await api.get("/jobs", { params });
      setRows(data.rows || []); setTotal(data.total || 0);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to load jobs");
    } finally { setBusy(false); }
  }, [q, statusFilter, workFilter, sourceFilter, postedPreset, sortBy, page]);
  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const s = { Active: 0, Expired: 0, Unavailable: 0, Total: rows.length };
    rows.forEach((r) => { if (s[r.job_status] !== undefined) s[r.job_status] += 1; });
    return s;
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="job-library-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Job Library</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Central catalogue of jobs discovered by the team. Every job entered here is available to all authorized staff.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={busy}
                  data-testid="jobs-refresh"
                  className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
            <ArrowsCounterClockwise size={14} /> Refresh
          </button>
          <button onClick={() => setShowBulk(true)}
                  data-testid="jobs-bulk-open"
                  className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
            <UploadSimple size={14} /> Bulk Import
          </button>
          <button onClick={() => setShowAdd(true)}
                  data-testid="jobs-add-open"
                  className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white">
            <Plus size={14} /> Add Job
          </button>
        </div>
      </div>

      {/* Summary counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total on page" value={summary.Total} testid="jobs-stat-total" />
        <StatCard label="Active" value={summary.Active} tone="emerald" testid="jobs-stat-active" />
        <StatCard label="Expired" value={summary.Expired} tone="amber" testid="jobs-stat-expired" />
        <StatCard label="Unavailable" value={summary.Unavailable} tone="red" testid="jobs-stat-unavailable" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
                 data-testid="jobs-search"
                 placeholder="Search title, company, location, skill"
                 className="w-full border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40" />
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                data-testid="jobs-status-filter"
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">All statuses</option>
          <option>Active</option><option>Expired</option><option>Closed</option>
          <option>Unavailable</option><option>Archived</option>
        </select>
        <select value={workFilter} onChange={(e) => { setWorkFilter(e.target.value); setPage(1); }}
                data-testid="jobs-work-filter"
                className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="">Any arrangement</option>
          <option>Remote</option><option>Hybrid</option><option>On-site</option>
        </select>
        <input value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
               data-testid="jobs-source-filter"
               placeholder="Source"
               className="border border-slate-200 rounded-md px-3 py-2 text-sm w-40" />
        <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                 data-testid="jobs-sort"
                 className="border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option value="discovered_desc">Recently Discovered</option>
          <option value="posted_desc">Newest Posted</option>
          <option value="posted_asc">Oldest Posted</option>
        </select>
        <div className="ml-auto text-xs text-slate-500">
          {busy ? "Loading…" : `${total} job${total === 1 ? "" : "s"} in library`}
        </div>
      </div>

      {/* Phase 13 Beat 4 — posted-date filter chips */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="jobs-posted-chips">
        <span className="text-[11px] text-slate-500 mr-1">Posted:</span>
        {POSTED_PRESETS.map((p) => (
          <button key={p.key} onClick={() => { setPostedPreset(p.key); setPage(1); }}
                   data-testid={`jobs-posted-${p.key}`}
                   className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                     postedPreset === p.key
                       ? "bg-slate-900 text-white border-slate-900"
                       : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                   }`}>{p.label}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-[0.1em]">
            <tr>
              <th className="text-left px-4 py-2.5">Title / Company</th>
              <th className="text-left px-4 py-2.5">Location</th>
              <th className="text-left px-4 py-2.5">Arrangement</th>
              <th className="text-left px-4 py-2.5">Source</th>
              <th className="text-left px-4 py-2.5">Posted</th>
              <th className="text-left px-4 py-2.5">Discovered</th>
              <th className="text-left px-4 py-2.5">Reused</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !busy && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-slate-400" data-testid="jobs-empty">
                No jobs match the current filters. Click <b>Add Job</b> to enter your first URL.
              </td></tr>
            )}
            {rows.map((r) => {
              const tone = STATUS_TONE[r.job_status] || STATUS_TONE.Active;
              return (
                <tr key={r.id} data-testid={`job-row-${r.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/jobs/${r.id}`} className="font-medium text-slate-900 hover:text-sky-700"
                          data-testid={`job-title-${r.id}`}>
                      {r.job_title || "Untitled"}
                    </Link>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <Buildings size={11} /> {r.company || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    <div className="flex items-center gap-1"><MapPin size={11} /> {r.location || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.work_arrangement || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    <div className="inline-flex items-center gap-1">
                      <LinkIcon size={11} /> {r.source || r.source_type || "manual"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs"
                      title={formatPostedAbsolute(r.originally_posted_at) || "Employer posting date not available"}
                      data-testid={`job-posted-${r.id}`}>
                    {formatPostedAge(r.originally_posted_at)}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    <div>{r.discovered_by_name || "—"}</div>
                    <div className="text-[11px] text-slate-400">{fmtDate(r.discovered_at)}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {(r.reuse_count || 0) > 0 ? (
                      <span className="inline-flex items-center gap-1 text-indigo-700"
                            data-testid={`job-reused-${r.id}`}>
                        <ShareNetwork size={11} /> {r.reuse_count}×
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${tone}`}
                          data-testid={`job-status-${r.id}`}>
                      {r.job_status || "Active"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/jobs/${r.id}`}
                          data-testid={`job-open-${r.id}`}
                          className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-100 text-slate-700">
                      Open
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {total > pageSize && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Page {page} of {Math.ceil(total / pageSize)}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                    className="px-3 py-1.5 rounded border border-slate-200 disabled:opacity-50">Previous</button>
            <button disabled={page * pageSize >= total} onClick={() => setPage(page + 1)}
                    className="px-3 py-1.5 rounded border border-slate-200 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {showAdd && <AddJobDialog onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
      {showBulk && <BulkJobImportDialog onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); load(); }} />}
    </div>
  );
}

function StatCard({ label, value, tone = "slate", testid }) {
  const toneMap = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <div className={`border rounded-xl px-4 py-3 ${toneMap[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
